
import React, { useState, useEffect } from 'react';
import { Step } from 'react-joyride';
import { UserTour } from './UserTour';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

interface DashboardTourProps {
  onComplete?: () => void;
}

// Fallback steps in case database is unavailable
const fallbackSteps: Step[] = [
  {
    target: '[data-tour="dashboard-header-center"]',
    content: 'Welcome to your dashboard! This is your central hub where you can see your profile and role information.',
    placement: 'bottom',
  },
  {
    target: '[data-sidebar="trigger"]',
    content: 'Use this menu to navigate between different sections like your devices, profile, and settings.',
    placement: 'right',
  },
  {
    target: '.device-grid',
    content: 'Here you can view and manage all your assigned devices. Click on any device to see its detailed information.',
    placement: 'top',
  },
  {
    target: 'a[href="/dashboard/profile"]',
    content: 'This is your profile information, you can change or update your personal information here',
    placement: 'right',
  },
  {
    target: 'a[href="/dashboard/notifications"]',
    content: 'This is where you will find notifications which includes proposals',
    placement: 'right',
  },
  {
    target: 'a[href="/dashboard/credit-management"]',
    content: 'This is where you will find your credits and can assign available credits to your devices',
    placement: 'right',
  },
];

export const DashboardTour: React.FC<DashboardTourProps> = ({ onComplete }) => {
  const [runTour, setRunTour] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch tour steps from database
  const { data: tourSteps, isLoading } = useQuery({
    queryKey: ['dashboard-tour-steps'],
    queryFn: async () => {
      const { data: tour, error: tourError } = await supabase
        .from('tours')
        .select('id')
        .eq('target_page', 'dashboard')
        .eq('is_active', true)
        .single();

      if (tourError || !tour) {
        console.log('No active dashboard tour found, using fallback steps');
        return fallbackSteps;
      }

      const { data: steps, error: stepsError } = await supabase
        .from('tour_steps')
        .select('*')
        .eq('tour_id', tour.id)
        .order('step_order');

      if (stepsError || !steps) {
        console.log('Error loading tour steps, using fallback steps');
        return fallbackSteps;
      }

      // Convert database steps to Joyride format and ensure first step targets header center
      const joyrideSteps: Step[] = steps.map((step, index) => {
        if (index === 0) {
          // Force the first step to always start at header center
          return {
            target: '[data-tour="dashboard-header-center"]',
            content: step.content,
            placement: step.placement as any,
            ...(step.options && typeof step.options === 'object' ? step.options : {})
          };
        }
        return {
          target: step.target,
          content: step.content,
          placement: step.placement as any,
          ...(step.options && typeof step.options === 'object' ? step.options : {})
        };
      });

      console.log('Loaded tour steps from database:', joyrideSteps);
      return joyrideSteps;
    },
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  const dashboardSteps = tourSteps || fallbackSteps;

  // Helper function to check if tour should start
  // The tour NEVER starts automatically - it only runs when explicitly triggered
  // via a manual restart (useTourRestart sets the flag + navigates here) or a
  // ?startTour=true URL parameter.
  const shouldStartTour = () => {
    if (!user) return false;

    // Check for URL parameter
    const urlParams = new URLSearchParams(location.search);
    const startTourParam = urlParams.get('startTour');
    
    // Check for manual restart flag
    const manualRestart = localStorage.getItem(`dashboard_tour_manual_restart_${user.id}`);
    
    console.log('🎯 [DashboardTour] Tour trigger check:', {
      startTourParam,
      manualRestart,
      userId: user.id
    });

    // Only explicit triggers start the tour - no automatic start for new users
    if (startTourParam === 'true' || manualRestart === 'true') {
      console.log('🚀 [DashboardTour] Starting tour due to explicit manual trigger');
      return true;
    }

    console.log('❌ [DashboardTour] No explicit trigger, tour stays off');
    return false;
  };

  useEffect(() => {
    console.log('🔍 [DashboardTour] useEffect triggered', { 
      user: !!user, 
      userId: user?.id, 
      userEmail: user?.email,
      locationSearch: location.search,
      currentPath: location.pathname,
      dashboardSteps: dashboardSteps?.length
    });
    
    if (!user) {
      console.log('❌ [DashboardTour] No user found, exiting');
      return;
    }

    // Force immediate check for debugging
    console.log('🔍 [DashboardTour] Current user details:', {
      id: user.id,
      email: user.email,
      created_at: user.created_at
    });

    // Check localStorage values for debugging
    const localStorageCompleted = localStorage.getItem(`dashboard_tour_completed_${user.id}`);
    const manualRestart = localStorage.getItem(`dashboard_tour_manual_restart_${user.id}`);
    console.log('🔍 [DashboardTour] localStorage values:', {
      completed: localStorageCompleted,
      manualRestart: manualRestart
    });

    const startTour = shouldStartTour();
    
    if (startTour) {
      // Clear URL parameter if present
      const urlParams = new URLSearchParams(location.search);
      if (urlParams.get('startTour')) {
        urlParams.delete('startTour');
        const newUrl = `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        navigate(newUrl, { replace: true });
      }

      // Clear manual restart flag
      localStorage.removeItem(`dashboard_tour_manual_restart_${user.id}`);

      console.log('🎯 [DashboardTour] Preparing to start tour...');
      
      // Longer delay to ensure DOM elements are properly rendered
      const timer = setTimeout(() => {
        // Only verify the first target exists (header center) before starting tour
        const firstTarget = dashboardSteps[0]?.target as string;
        const firstElement = document.querySelector(firstTarget);
        console.log(`🎯 [DashboardTour] Checking first target: ${firstTarget}`, firstElement);
        
        if (firstElement) {
          console.log('✅ [DashboardTour] Starting tour - first target found');
          setRunTour(true);
        } else {
          console.warn('⚠️ [DashboardTour] First tour target not found, retrying...');
          // Retry after another delay
          setTimeout(() => {
            console.log('🔄 [DashboardTour] Retrying tour start...');
            setRunTour(true);
          }, 1000);
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    } else {
      console.log('❌ [DashboardTour] Tour conditions not met, skipping');
    }
  }, [user, location.search, dashboardSteps, navigate]);

  const handleTourComplete = async () => {
    try {
      // Mark tour as completed in localStorage
      localStorage.setItem(`dashboard_tour_completed_${user?.id}`, 'true');
      
      // Also mark as completed in database
      if (user?.id) {
        const { error } = await supabase
          .from('profiles')
          .update({ dashboard_tour_completed: true })
          .eq('user_id', user.id);
        
        if (error) {
          console.error('❌ [DashboardTour] Error updating database tour completion:', error);
        } else {
          console.log('✅ [DashboardTour] Tour marked as completed in database');
        }
      }
      
      console.log('✅ [DashboardTour] Tour marked as completed');
    } catch (error) {
      console.error('❌ [DashboardTour] Error updating tour completion status:', error);
    }

    setRunTour(false);
    onComplete?.();
  };

  // Function to restart tour - updates localStorage and triggers tour
  const restartTour = async () => {
    try {
      // Reset tour completion status and set manual restart flag
      localStorage.removeItem(`dashboard_tour_completed_${user?.id}`);
      localStorage.setItem(`dashboard_tour_manual_restart_${user?.id}`, 'true');
      console.log('🔄 [DashboardTour] Tour status reset, starting tour...');
      setRunTour(true);
    } catch (error) {
      console.error('❌ [DashboardTour] Error resetting tour status:', error);
    }
  };

  // Expose restart function globally for easy access
  React.useEffect(() => {
    (window as any).restartDashboardTour = restartTour;
    return () => {
      delete (window as any).restartDashboardTour;
    };
  }, []);

  return (
    <UserTour
      run={runTour}
      onComplete={handleTourComplete}
      steps={dashboardSteps}
    />
  );
};
